import React, { useState } from 'react';
import { TipSummary } from '../../layout/FundedTips/TipSummary';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import { Skeleton } from '@material-ui/lab';

import { useStyles } from './LookupPage.styles';
import { useOrderReceipt } from '../../../hooks/useCustomerLookup/useCustomerLookup';
import { enqueueSnackbar } from '../../../redux/ducks/SnackbarDucks';
import EditPhoneDialog from './EditPhoneDialog';
import EditAddressDialog from './EditAddressDialog';
import { formatOrderEvent, parseOrderEvent } from '../../../utils/formatOrderEvent';

const formatCurrency = (val) =>
  val != null ? `$${Number(val).toFixed(2)}` : null;

const STATUS_COLORS = {
  placed: '#1976d2',
  confirmed: '#388e3c',
  delivered: '#2e7d32',
  cancelled: '#d32f2f',
  'picked up': '#f57c00',
};

const TIMELINE_FIELDS = [
  { key: 'placedAtUtc', label: 'Placed' },
  { key: 'confirmedAtUtc', label: 'Confirmed' },
  { key: 'pickedUpAtUtc', label: 'Picked up' },
  { key: 'deliveredAtUtc', label: 'Delivered' },
  { key: 'cancelledAtUtc', label: 'Cancelled' },
];

function SectionHeader({ label, onEdit }) {
  const classes = useStyles();
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography className={classes.panelSectionHeader}>{label}</Typography>
      {onEdit && (
        <IconButton size="small" onClick={onEdit} aria-label={`edit ${label.toLowerCase()}`}>
          <EditIcon style={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}

SectionHeader.propTypes = {
  label: PropTypes.string.isRequired,
  onEdit: PropTypes.func,
};

SectionHeader.defaultProps = {
  onEdit: null,
};

function PanelLoadingSkeleton({ query, onClose }) {
  const classes = useStyles();
  return (
    <Box className={classes.panelContainer}>
      <Box className={classes.panelHeader}>
        <Box flex={1}>
          <Typography className={classes.panelHeaderTitle}>
            Order: {query}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close order panel">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className={classes.panelBody}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Box key={i} className={classes.skeletonWrapper}>
            <Skeleton variant="text" width="45%" height={20} />
            <Skeleton
              variant="text"
              width="75%"
              height={16}
              style={{ marginTop: 4 }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

PanelLoadingSkeleton.propTypes = {
  query: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export function OrderReceiptPanel({ query, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { data, isLoading, error, refetch } = useOrderReceipt(query);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);

  const handleEditSuccess = (message) => {
    dispatch(enqueueSnackbar({ message, options: { variant: 'success' } }));
    refetch();
  };

  const handleEditError = (message) => {
    dispatch(enqueueSnackbar({ message, options: { variant: 'error' } }));
  };

  if (isLoading) {
    return <PanelLoadingSkeleton query={query} onClose={onClose} />;
  }

  if (error || !data) {
    return (
      <Box className={classes.panelContainer}>
        <Box className={classes.panelHeader}>
          <Box flex={1}>
            <Typography className={classes.panelHeaderTitle}>
              Order: {query}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="close order panel">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className={classes.errorContainer}>
          <Typography variant="body2" color="error">
            {error
              ? 'Failed to load order receipt.'
              : 'No receipt data available.'}
          </Typography>
          <Box display="flex" style={{ gap: 8 }}>
            {error && (
              <Button size="small" variant="contained" color="primary" onClick={() => refetch()}>
                Retry
              </Button>
            )}
            <Button size="small" variant="outlined" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  const statusColor =
    STATUS_COLORS[data.status?.toLowerCase()] || '#757575';

  const timeline = data.timeline || {};
  const restaurant = data.restaurant || {};
  const customer = data.customer || {};
  const deliveryAddress = data.deliveryAddress || {};
  const financials = data.financials || {};
  const items = data.items || [];
  const driver = data.driver;

  const isEditable = data.statusId != null && data.statusId >= 1 && data.statusId <= 8;

  const isCancelled = /^cancelled$|^canceled$/i.test(data.status || '');
  const timelineEntries = TIMELINE_FIELDS.map(({ key, label }) => ({
    label,
    ts: timeline[key],
  }));

  const financialRows = [
    { label: 'Subtotal', value: financials.subtotal },
    { label: 'Delivery fee', value: financials.deliveryFee },
    { label: 'Service fee', value: financials.serviceFee },
    { label: 'Tax', value: financials.tax },
    {
      label: 'Adjustment',
      value: financials.adjustment,
      note: financials.adjustmentNote,
    },
    { label: 'Coupon discount', value: financials.couponDiscount },
    {
      label: 'Gift certificate',
      value: financials.giftCertificateDiscount,
    },
  ].filter(
    ({ value, note }) => (value != null && value !== 0) || note
  );

  const displayId = data.displayId || String(data.id || query);
  const orderTypeLine = [data.orderType, data.deliveryType]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box className={classes.panelContainer}>
      <Box className={classes.panelHeader}>
        <Box flex={1}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Typography className={classes.panelHeaderTitle}>
              {displayId}
            </Typography>
            <Chip
              label={data.status || '—'}
              size="small"
              style={{
                backgroundColor: statusColor,
                color: '#fff',
                height: 20,
                fontSize: '0.72rem',
              }}
            />
          </Box>
          {orderTypeLine && (
            <Typography variant="caption" color="textSecondary">
              {orderTypeLine}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close order panel">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.panelBody}>
        <Box className={classes.panelSection}>
          <TipSummary order={data} />
        </Box>
        {data.cancelNote && (
          <Box className={classes.cancelNoteBox}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              Cancellation reason
            </Typography>
            <Typography variant="body2">{data.cancelNote}</Typography>
          </Box>
        )}

        {timelineEntries.length > 0 && (
          <>
            <Box className={classes.panelSection}>
              <SectionHeader label="Timeline" />
              {timelineEntries.map(({ label, ts }) => (
                <Box key={label} className={classes.panelInfoRow}>
                  <Typography variant="body2" color="textSecondary">
                    {label}
                  </Typography>
                  <Typography variant="body2">
                    {label === 'Cancelled' && isCancelled && !parseOrderEvent(ts)
                      ? 'Cancelled — time unavailable'
                      : formatOrderEvent(ts, restaurant.timezone)}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Divider />
          </>
        )}

        <Box className={classes.panelSection}>
          <SectionHeader label="Restaurant" />
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {restaurant.name || '—'}
          </Typography>
          {restaurant.address && (
            <Typography variant="body2" color="textSecondary">
              {[restaurant.address, restaurant.city, restaurant.state, restaurant.zip]
                .filter(Boolean)
                .join(', ')}
            </Typography>
          )}
          {restaurant.phone && (
            <Typography variant="body2" color="textSecondary">
              {restaurant.phone}
            </Typography>
          )}
        </Box>

        <Divider />

        <Box className={classes.panelSection}>
          <SectionHeader
            label="Customer"
            onEdit={isEditable ? () => setPhoneDialogOpen(true) : undefined}
          />
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {[customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
              '—'}
          </Typography>
          {customer.phone && (
            <Typography variant="body2" color="textSecondary">
              {customer.phone}
            </Typography>
          )}
        </Box>

        {deliveryAddress.address1 && (
          <>
            <Divider />
            <Box className={classes.panelSection}>
              <SectionHeader
                label="Delivery Address"
                onEdit={isEditable ? () => setAddressDialogOpen(true) : undefined}
              />
              <Typography variant="body2">
                {[
                  deliveryAddress.address1,
                  deliveryAddress.city,
                  deliveryAddress.state,
                  deliveryAddress.zip,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
            </Box>
          </>
        )}

        <Divider />

        <Box className={classes.panelSection}>
          <SectionHeader label="Items" />
          {items.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No items
            </Typography>
          ) : (
            items.map((item) => (
              <Box key={item.id} className={classes.itemRow}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="body2" style={{ flex: 1 }}>
                    {item.quantity}× {item.name}
                    {item.label ? ` (${item.label})` : ''}
                  </Typography>
                  <Typography variant="body2" style={{ whiteSpace: 'nowrap', marginLeft: 8 }}>
                    {formatCurrency(
                      item.unitPrice != null
                        ? item.unitPrice * (item.quantity || 1)
                        : null
                    ) || '—'}
                  </Typography>
                </Box>
                {item.specialInstructions && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: 'block' }}
                  >
                    Note: {item.specialInstructions}
                  </Typography>
                )}
                {item.options && item.options.length > 0 && (
                  <Box pl={1} mt={0.25}>
                    {item.options.map((opt) => (
                      <Typography
                        key={opt.id}
                        variant="caption"
                        color="textSecondary"
                        style={{ display: 'block' }}
                      >
                        {opt.questionText}: {opt.answerText}
                        {opt.price != null && opt.price !== 0
                          ? ` (+${formatCurrency(opt.price)})`
                          : ''}
                        {opt.specialInstructions
                          ? ` — ${opt.specialInstructions}`
                          : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ))
          )}
        </Box>

        <Divider />

        <Box className={classes.panelSection}>
          <SectionHeader label="Financials" />
          {financialRows.map(({ label, value, note }) => (
            <Box key={label} className={classes.panelInfoRow}>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {label}
                </Typography>
                {note && (
                  <Typography variant="caption" color="textSecondary">
                    {note}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2">
                {formatCurrency(value) || '—'}
              </Typography>
            </Box>
          ))}
          <Divider className={classes.financialDivider} />
          <Box className={classes.panelInfoRow}>
            <Typography variant="body2" style={{ fontWeight: 700 }}>
              Order total
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 700 }}>
              {formatCurrency(financials.orderTotal) || '—'}
            </Typography>
          </Box>
          {financials.totalPaid != null && (
            <Box className={classes.panelInfoRow}>
              <Typography variant="body2" color="textSecondary">
                Total paid
              </Typography>
              <Typography variant="body2">
                {formatCurrency(financials.totalPaid)}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        <Box className={classes.panelSection}>
          {data.payment?.type && (
            <Box className={classes.panelInfoRow} mb={0.5}>
              <Typography variant="body2" color="textSecondary">
                Payment
              </Typography>
              <Typography variant="body2">{data.payment.type}</Typography>
            </Box>
          )}
          <Box className={classes.panelInfoRow}>
            <Typography variant="body2" color="textSecondary">
              Driver
            </Typography>
            <Typography variant="body2">
              {driver
                ? [driver.firstName, driver.lastName]
                    .filter(Boolean)
                    .join(' ') +
                  (driver.phone ? ` · ${driver.phone}` : '')
                : 'No driver assigned'}
            </Typography>
          </Box>
          {data.deliveryNotes && (
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary">
                Delivery notes
              </Typography>
              <Typography variant="body2">{data.deliveryNotes}</Typography>
            </Box>
          )}
          {data.dispatchNotes && (
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary">
                Dispatch notes
              </Typography>
              <Typography variant="body2">{data.dispatchNotes}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {data.id && (
        <>
          <EditPhoneDialog
            open={phoneDialogOpen}
            onClose={() => setPhoneDialogOpen(false)}
            orderId={data.id}
            customerId={customer.id}
            currentPhone={customer.phone}
            onSuccess={handleEditSuccess}
            onError={handleEditError}
          />
          <EditAddressDialog
            open={addressDialogOpen}
            onClose={() => setAddressDialogOpen(false)}
            orderId={data.id}
            customerId={customer.id}
            currentAddress={deliveryAddress}
            onSuccess={handleEditSuccess}
            onError={handleEditError}
          />
        </>
      )}
    </Box>
  );
}

OrderReceiptPanel.propTypes = {
  query: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};
